(ns core.feature-flag.model
  (:require [potpuri.core :as p]
            [schema.core :as s]))

(def flags-to-always-enable
  "Flags scheduled for removal that must stay enabled, including in E2E tests.
   QA maintains this set after production rollout and updating the E2E baseline."
  #{:feature-flag.id/optimized-bookings-for-dispatch-query
    :feature-flag.id/upgraded-sortable-tables
    :feature-flag.id/replace-editable-attr
    :feature-flag.id/inspection-visit-configuration
    :feature-flag.id/simplify-inspection-type-specification-structure
    :feature-flag.id/delete-generic-letters
    :feature-flag.id/standard-texts-admin-view
    :feature-flag.id/pre-con-meeting-guard
    :feature-flag.id/cr-expired-actions
    :feature-flag.id/duplicate-import-session
    :feature-flag.id/revert-from-approved
    :feature-flag.id/pre-con-multi-applications
    :feature-flag.id/streamline-ce-admin-process
    :feature-flag.id/combobox-in-complaint-dashboard
    :feature-flag.id/allow-party-removal
    :feature-flag.id/complaint-photos
    :feature-flag.id/fee-based-variables
    :feature-flag.id/formula-variables
    :feature-flag.id/dashboard-search-functions
    :feature-flag.id/public-works-public-map
    :feature-flag.id/fee-alpha-fields
    :feature-flag.id/checkout-page-v2
    :feature-flag.id/update-bill-due-date
    :feature-flag.id/fees-if-function
    :feature-flag.id/public-language-selector
    :feature-flag.id/back-to-submitted-enhancements
    :feature-flag.id/allow-multiple-categories-to-a-case
    :feature-flag.id/licence-numbering-by-config-tree
    :feature-flag.id/emails-in-audit-log
    :feature-flag.id/check-permit-template-sign-off
    :feature-flag.id/business-foreign-address
    :feature-flag.id/authority-user-manages-users-in-approvers-groups
    :feature-flag.id/calendar-subscription-link
    :feature-flag.id/allow-circulation-in-bld-and-pw
    :feature-flag.id/periodic-inspections
    :feature-flag.id/payment-emails
    :feature-flag.id/enhanced-order-templates
    :feature-flag.id/multi-select-issued-to
    :feature-flag.id/add-cheque-number
    :feature-flag.id/bill-invoice-numbering
    :feature-flag.id/multiple-inspection-bookings
    :feature-flag.id/rich-text-in-applicant-review-comments
    :feature-flag.id/dispatch-assign
    :feature-flag.id/assigned-review-email
    :feature-flag.id/inspection-restriction
    :feature-flag.id/extended-property-data
    :feature-flag.id/property-attachments
    :feature-flag.id/property-relations-view
    :feature-flag.id/configure-gis-source
    :feature-flag.id/property-attributes-gis-link
    :feature-flag.id/revised-payments-report
    :feature-flag.id/bill-reverse
    :feature-flag.id/let-assigned-payer-pay-the-bill
    :feature-flag.id/prevent-request-when-unpaid
    :feature-flag.id/bill-reversal-methods
    :feature-flag.id/update-bill-due-date-to-past
    :feature-flag.id/new-tags
    :feature-flag.id/create-workspace-with-manual-location
    :feature-flag.id/inspection-type-table-v2
    :feature-flag.id/inspection-stakeholder-notifications
    :feature-flag.id/pwo-notification-flags
    :feature-flag.id/sub-review-modified-ts
    :feature-flag.id/public-work-orders-public-map
    :feature-flag.id/complaints-main-menu-enhancements
    :feature-flag.id/complaint-permit-type-selection
    :feature-flag.id/complaint-sign-off-may-be-required
    :feature-flag.id/complaint-submission-configuration
    :feature-flag.id/complaints-priority
    :feature-flag.id/complaints-wo-property
    :feature-flag.id/ce-and-pwo-translations
    :feature-flag.id/add-complainant-as-related-party
    :feature-flag.id/public-notice-in-execution
    :feature-flag.id/inspection-priority
    :feature-flag.id/inspection-created-due-date
    :feature-flag.id/reassign-from-modify-request-modal
    :feature-flag.id/outstanding-bill-close-inspection
    :feature-flag.id/bonds
    :feature-flag.id/create-property-from-workspace
    :feature-flag.id/checklist-configuration-v2
    :feature-flag.id/pdf-validation-only-by-default
    :feature-flag.id/approver-group-creation-for-domains
    :feature-flag.id/set-bill-unpaid
    :feature-flag.id/reporting-content-packages
    :feature-flag.id/enforcement-inspection-report-checklists
    :feature-flag.id/standard-texts-import
    :feature-flag.id/version-specific-conversion-info
    :feature-flag.id/form-editor-tables
    :feature-flag.id/permit-pdf-table-form-fields
    :feature-flag.id/requested-date-on-bookings
    :feature-flag.id/inspection-dependencies
    :feature-flag.id/comply-date-restriction
    :feature-flag.id/comply-by-date-notifications})

(defn- feature-flag-id-keyword? [v]
  (and (keyword? v)
       (= (namespace v) "feature-flag.id")))

(s/defschema FeatureFlagIdKeyword
  (s/pred feature-flag-id-keyword? 'feature-flag-id-keyword?))

(s/defschema FeatureFlagModel
  {:feature-flag/id                                FeatureFlagIdKeyword
   :feature-flag-model/description                 s/Str
   (s/optional-key :feature-flag-model/jira-issue) s/Str
   (s/optional-key :feature-flag-model/epic)       s/Str
   (s/optional-key :feature-flag-model/depends-on) #{FeatureFlagIdKeyword}})

(def epic-titles
  {"WIN-1132" "Security fixes for vaahtera"
   "WIN-1619" "Inspections improvement II"
   "WIN-2922" "PLF: Enabling units in property database"
   "WIN-3191" "Municipal user controls (email) notifications - additions"
   "WIN-3399" "ICC Code Connect"
   "WIN-3503" "UX improvements and bug fixes H2/24"
   "WIN-3567" "CBE: Enhancements-Import violations, email notifications for assignment, and show property on a notecard"
   "WIN-3616" "Use single pre-consultation for multiple workspaces"
   "WIN-3620" "Property Data Management R1.0"
   "WIN-3637" "Property Database - R1.1 - GIS"
   "WIN-3638" "Property Database - R1.1 - Attachments"
   "WIN-3669" "Reoccurring Periodic Inspections R1.0"
   "WIN-3700" "Recurring Periodic Inspections R1.1 - Enhanced Inspection Deficiency and Photo Management"
   "WIN-3704" "PLF: Bond Management 1"
   "WIN-3708" "Circulation Email Tracking"
   "WIN-3725" "Fees Management Enhancements 1"
   "WIN-3733" "Configuration Import/Export changes"
   "WIN-3802" "PLF: Inspections in the pre-issuance phases/stages"
   "WIN-3863" "Property Data Management R1.1"
   "WIN-3864" "Recurring Periodic Inspections R1.1"
   "WIN-3868" "Squid Squad Misc Q1 '25"
   "WIN-3893" "Octopus Ops Misc. Q3 '25"
   "WIN-3904" "Periodic Inspection R1.1 - Inspection Type business rules"
   "WIN-3905" "Reoccurring Periodic Inspections R1.1 - Qualification Improvements"
   "WIN-3912" "Periodic inspection R1.1 - Orders issuance to Multi-Parties"
   "WIN-3973" "Fees Management Enhancements 2"
   "WIN-4000" "Public Works Phase 2 General Enhancements"
   "WIN-4051" "Property Data Management R1.2"
   "WIN-4056" "Periodic Inspection R1.1 - Display date when the inspection was requested"
   "WIN-4078" "PLF: Reviews Misc"
   "WIN-4106" "Periodic Inspection R1.1 - Comply by date automation functionality"
   "WIN-4115" "Remove Attachments PDF/A"
   "WIN-4165" "Public Works Phase 2 GIS Enhancements Epic"
   "WIN-4206" "Recuring Periodic Inspections R1.2 – Checklist Enhancements"
   "WIN-4207" "Recurring Periodic Inspections R1.2 – Business rule enhancements"
   "WIN-4208" "RPI 1.3 - Inspection Label Standards, Localization, Classification & Management"
   "WIN-4221" "Planning Process Flow Changes"
   "WIN-4246" "Navigation Improvement"
   "WIN-4256" "PLF: UX Workspace Search & Tools Update"
   "WIN-4272" "Security fixes for vaahtera R2"
   "WIN-4284" "Phase 1 Inspector AI"
   "WIN-4329" "BlueBeam integration"
   "WIN-4358" "PLF: Meetings - Domain Organizational Level"
   "WIN-4377" "Work Orders 1.0 - Complaints"
   "WIN-4378" "Work Orders 1.0 - Workspace"
   "WIN-4379" "Work Orders 1.0 - Work Tasks"
   "WIN-4380" "Work Orders 1.0 - Administration"
   "WIN-4428" "Shared Services R1"
   "WIN-4534" "Shared Services (SSG) R2"
   "WIN-4585" "PLF: Review Enhancements"
   "WIN-4637" "PLF : Express Issuance"
   "WIN-4647" "System PDF Markup Tool"
   "WIN-4660" "PLF: License and Contractor Registration Renewal Management"
   "WIN-4683" "PLF: Integrated Products R1"
   "WIN-4684" "Configurable Email Templates R1 (Custom System Emails)"
   "WIN-4688" "Quick Pick - Inspector AI"
   "WIN-4689" "Licensing Improvements (2025)"
   "WIN-4714" "Property Data Integrity Tooling (Property DB v3)"
   "WIN-4754" "PLF: Product Default Updates Best Practice (OOB Solution)"
   "WIN-4775" "Landing Page (Not signed in)"
   "WIN-4776" "Workspace Update - Full Overhaul"
   "WIN-4777" "Applicant Wizard Flow"
   "WIN-4780" "AI Support: Modifications to the Flow and Design"
   "WIN-4818" "AI Chatbot"
   "WIN-5115" "SSG R3 - Enhanced Shared Services"
   "WIN-5516" "Devbot Tooling"
   "WIN-5599" "PLA: User-defined Circulation Groups, Bounce-back reminder, condition due dates"
   "WIN-5602" "Attachments-section Update (Wizard + WP)"
   "WIN-5734" "Unified tenant and user management"
   "WIN-5739" "AI Post Check"})

(def flags
  (s/validate [FeatureFlagModel]
              [{:feature-flag/id                :feature-flag.id/delete-generic-letters
                :feature-flag-model/description "Allow group admin to delete generic letters"
                :feature-flag-model/jira-issue  "WIN-3481"}
               {:feature-flag/id                :feature-flag.id/standard-texts-admin-view
                :feature-flag-model/description "Enable \"Standard texts\" municipality admin view for phrases and ICC / model code management"
                :feature-flag-model/jira-issue  "WIN-3400"
                :feature-flag-model/epic        "WIN-3399"}
               {:feature-flag/id                :feature-flag.id/optimized-bookings-for-dispatch-query
                :feature-flag-model/description "Use optimized booking query for dispatchers"
                :feature-flag-model/jira-issue  "WIN-3565"}
               {:feature-flag/id                :feature-flag.id/pre-con-meeting-guard
                :feature-flag-model/description "Require pre-con meetings to be handled before proceeding to complete"
                :feature-flag-model/jira-issue  "WIN-3487"}
               {:feature-flag/id                :feature-flag.id/cr-expired-actions
                :feature-flag-model/description "Allow renew and revoke for expired contractor registrations"
                :feature-flag-model/jira-issue  "TT-23997"}
               {:feature-flag/id                :feature-flag.id/duplicate-import-session
                :feature-flag-model/description "Duplication functionality for data import sessions"
                :feature-flag-model/jira-issue  "WIN-3505"}
               {:feature-flag/id                :feature-flag.id/revert-from-approved
                :feature-flag-model/description "Allow municipal administrator to move an approved planning WS back to approval"
                :feature-flag-model/jira-issue  "TT-24040"}
               {:feature-flag/id                :feature-flag.id/pre-con-multi-applications
                :feature-flag-model/description "Create multiple applications from a single pre-consultation"
                :feature-flag-model/jira-issue  "WIN-3215"
                :feature-flag-model/epic        "WIN-3616"}
               {:feature-flag/id                :feature-flag.id/streamline-ce-admin-process
                :feature-flag-model/description "Add option to skip initial inspection and complaint creation for admin (CE)"
                :feature-flag-model/jira-issue  "WIN-3575"
                :feature-flag-model/epic        "WIN-3567"}
               {:feature-flag/id                :feature-flag.id/combobox-in-complaint-dashboard
                :feature-flag-model/description "Use searchable group selection in complaint dashboard"
                :feature-flag-model/jira-issue  "WIN-3683"
                :feature-flag-model/epic        "WIN-3567"}
               {:feature-flag/id                :feature-flag.id/allow-party-removal
                :feature-flag-model/description "Allow authority to mark most parties removed in all phases"
                :feature-flag-model/jira-issue  "WIN-3310"}
               {:feature-flag/id                :feature-flag.id/complaint-photos
                :feature-flag-model/description "Add photos to a complaint and select them to Code Enforcement / By Law case"
                :feature-flag-model/jira-issue  "WIN-3577"
                :feature-flag-model/epic        "WIN-3567"}
               {:feature-flag/id                :feature-flag.id/inspection-visit-configuration
                :feature-flag-model/description "Add new Visits configuration tab under Inspections to configure visit types, results, reasons."
                :feature-flag-model/jira-issue  "WIN-3631"
                :feature-flag-model/epic        "WIN-3669"}
               {:feature-flag/id                :feature-flag.id/inspection-visit-configuration-mobile
                :feature-flag-model/description "Add new Visits configuration tab also to groups with mobile app enabled."
                :feature-flag-model/jira-issue  "WIN-3803"
                :feature-flag-model/epic        "WIN-3669"
                :feature-flag-model/depends-on  #{:feature-flag.id/inspection-visit-configuration}}
               {:feature-flag/id                :feature-flag.id/allow-circulation-in-bld-and-pw
                :feature-flag-model/description "Allow for circulation in the work and construction phase"
                :feature-flag-model/jira-issue  "WIN-3707"
                :feature-flag-model/epic        "WIN-3669"}
               {:feature-flag/id                :feature-flag.id/calendar-subscription-link
                :feature-flag-model/description "Allow user to generate a calendar link which they can add to email clients"
                :feature-flag-model/jira-issue  "WIN-2688"}
               {:feature-flag/id                :feature-flag.id/pre-consultation-conditions
                :feature-flag-model/description "Allow authority users to manage conditions during pre-consultation"
                :feature-flag-model/jira-issue  "WIN-5522"
                :feature-flag-model/epic        "WIN-5516"}
               {:feature-flag/id                :feature-flag.id/copy-letter-templates
                :feature-flag-model/description "Allow authority users to copy letter templates"
                :feature-flag-model/jira-issue  "WIN-5517"
                :feature-flag-model/epic        "WIN-5516"}
               {:feature-flag/id                :feature-flag.id/authority-user-manages-users-in-approvers-groups
                :feature-flag-model/description "Allow authority user to manage users within approvers groups that belong to their domain"
                :feature-flag-model/jira-issue  "WIN-3584"
                :feature-flag-model/epic        "WIN-3893"}
               {:feature-flag/id                :feature-flag.id/multi-circulation-requests
                :feature-flag-model/description "Allow selecting multiple approvers for circulation within the same approver group."
                :feature-flag-model/jira-issue  "WIN-3581"}
               {:feature-flag/id                :feature-flag.id/show-closure-categories
                :feature-flag-model/description "Show closure categories for all permit types"
                :feature-flag-model/jira-issue  "WIN-3781"}
               {:feature-flag/id                :feature-flag.id/circulation-table-sorting-and-filters
                :feature-flag-model/description "Allow authority user to sort and filter circulation requests and responses"
                :feature-flag-model/jira-issue  "WIN-3571"
                :feature-flag-model/epic        "WIN-3893"}
               {:feature-flag/id                :feature-flag.id/business-foreign-address
                :feature-flag-model/description "Add country and foreign address support to business parties"
                :feature-flag-model/jira-issue  "WIN-3804"
                :feature-flag-model/epic        "WIN-4689"}
               {:feature-flag/id                :feature-flag.id/check-permit-template-sign-off
                :feature-flag-model/description "In case sign-off is required after permit is issued, allow only permit draft template that has a sign-off section."
                :feature-flag-model/jira-issue  "WIN-3779"}
               {:feature-flag/id                :feature-flag.id/all-visits-inspection-requester
                :feature-flag-model/description "Add requester to the all visits section of inspection page"
                :feature-flag-model/jira-issue  "WIN-1630"
                :feature-flag-model/epic        "WIN-1619"}
               {:feature-flag/id                :feature-flag.id/property-management
                :feature-flag-model/description "Property management features, e.g. dashboard, property view and property search"
                :feature-flag-model/jira-issue  "WIN-3620"
                :feature-flag-model/epic        "WIN-3620"}
               {:feature-flag/id                :feature-flag.id/upgraded-sortable-tables
                :feature-flag-model/description "Upgrade modern responsive tables to sortable tables (Do NOT touch without consulting development)"
                :feature-flag-model/jira-issue  "WIN-3593"
                :feature-flag-model/epic        "WIN-3620"}
               {:feature-flag/id                :feature-flag.id/replace-editable-attr
                :feature-flag-model/description "Replace editable attribute with inline editable attribute"
                :feature-flag-model/jira-issue  "WIN-3257"
                :feature-flag-model/epic        "WIN-3503"}
               {:feature-flag/id                :feature-flag.id/new-tags
                :feature-flag-model/description "Enable new interactive tag component."
                :feature-flag-model/jira-issue  "WIN-3389"
                :feature-flag-model/epic        "WIN-3503"}
               {:feature-flag/id                :feature-flag.id/emails-in-audit-log
                :feature-flag-model/description "Add emails to the authority audit log"
                :feature-flag-model/jira-issue  "WIN-3748"
                :feature-flag-model/epic        "WIN-3708"}
               {:feature-flag/id                :feature-flag.id/licence-numbering-by-config-tree
                :feature-flag-model/description "Licence numbering by config tree"
                :feature-flag-model/jira-issue  "WIN-3848"
                :feature-flag-model/epic        "WIN-4689"}
               {:feature-flag/id                :feature-flag.id/allow-multiple-categories-to-a-case
                :feature-flag-model/description "Allow multiple complaint categories with a case type"
                :feature-flag-model/jira-issue  "WIN-3623"
                :feature-flag-model/epic        "WIN-3567"}
               {:feature-flag/id                :feature-flag.id/public-language-selector
                :feature-flag-model/description "Allow selecting the language on login page"
                :feature-flag-model/jira-issue  "WIN-2878"
                :feature-flag-model/epic        "WIN-3567"}
               {:feature-flag/id                :feature-flag.id/fees-if-function
                :feature-flag-model/description "Allow using IF function in fee formulas"
                :feature-flag-model/jira-issue  "WIN-3727"
                :feature-flag-model/epic        "WIN-3725"}
               {:feature-flag/id                :feature-flag.id/approval-delivery
                :feature-flag-model/description "Show request delivery status in circulation table."
                :feature-flag-model/jira-issue  "WIN-3570"
                :feature-flag-model/epic        "WIN-3893"}
               {:feature-flag/id                :feature-flag.id/update-bill-due-date
                :feature-flag-model/description "Update bill due date"
                :feature-flag-model/jira-issue  "WIN-3960"
                :feature-flag-model/epic        "WIN-3725"}
               {:feature-flag/id                :feature-flag.id/update-bill-due-date-to-past
                :feature-flag-model/description "Allow updating bill due date to past date (for testing)"
                :feature-flag-model/jira-issue  "WIN-4288"
                :feature-flag-model/epic        "WIN-3725"}
               {:feature-flag/id                :feature-flag.id/checkout-page-v2
                :feature-flag-model/description "Separate page and CSP for JS-based payment providers"
                :feature-flag-model/jira-issue  "WIN-4037"
                :feature-flag-model/epic        "WIN-1132"}
               {:feature-flag/id                :feature-flag.id/committee-meetings-for-b-and-ce
                :feature-flag-model/description "Committee meetings for B and CE permits"
                :feature-flag-model/jira-issue  "WIN-3595"}
               {:feature-flag/id                :feature-flag.id/periodic-inspections
                :feature-flag-model/description "Periodic inspections"
                :feature-flag-model/jira-issue  "WIN-3669"
                :feature-flag-model/epic        "WIN-3669"}
               {:feature-flag/id                :feature-flag.id/restrict-workspace-access-to-sibling-authorities
                :feature-flag-model/description "Allow restricting workspace access to sibling authorities by default"
                :feature-flag-model/jira-issue  "WIN-3890"
                :feature-flag-model/epic        "WIN-3893"}
               {:feature-flag/id                :feature-flag.id/grace-period?
                :feature-flag-model/description "Allow grace period in group config"
                :feature-flag-model/jira-issue  "WIN-3729"
                :feature-flag-model/epic        "WIN-3725"}
               {:feature-flag/id                :feature-flag.id/penalty-and-interest
                :feature-flag-model/description "Allow penalty and interest configuration in fee item"
                :feature-flag-model/jira-issue  "WIN-3723"
                :feature-flag-model/epic        "WIN-3725"}
               {:feature-flag/id                :feature-flag.id/fee-alpha-fields
                :feature-flag-model/description "Allow using alpha fields (any form field) in fee formulas"
                :feature-flag-model/jira-issue  "WIN-3728"
                :feature-flag-model/epic        "WIN-3725"}
               {:feature-flag/id                :feature-flag.id/public-works-public-map
                :feature-flag-model/description "Public map for public works"
                :feature-flag-model/jira-issue  "WIN-4127"
                :feature-flag-model/epic        "WIN-4000"}
               {:feature-flag/id                :feature-flag.id/dashboard-search-functions
                :feature-flag-model/description "Dashboard search functions"
                :feature-flag-model/jira-issue  "WIN-3987"
                :feature-flag-model/epic        "WIN-3669"}
               {:feature-flag/id                :feature-flag.id/deficiency-photo-numbering
                :feature-flag-model/description "Inspection deficiency and photo numbering"
                :feature-flag-model/jira-issue  "WIN-3911"
                :feature-flag-model/epic        "WIN-3700"}
               {:feature-flag/id                :feature-flag.id/mobile-periodic-inspections
                :feature-flag-model/description "Enable periodic inspection workspaces for mobile app"
                :feature-flag-model/jira-issue  "WIN-3984"
                :feature-flag-model/epic        "WIN-3669"}
               {:feature-flag/id                :feature-flag.id/fee-based-variables
                :feature-flag-model/description "Fees as a source for a variable UI"
                :feature-flag-model/jira-issue  "WIN-4040"
                :feature-flag-model/epic        "WIN-3725"}
               {:feature-flag/id                :feature-flag.id/multi-select-properties-for-periodic-workspace-creations
                :feature-flag-model/description "Enable multi-select properties for periodic workspace creations"
                :feature-flag-model/jira-issue  "WIN-3965"
                :feature-flag-model/epic        "WIN-3864"}
               {:feature-flag/id                :feature-flag.id/extended-property-data
                :feature-flag-model/description "Extended property data view"
                :feature-flag-model/jira-issue  "WIN-3863"
                :feature-flag-model/epic        "WIN-3863"}
               {:feature-flag/id                :feature-flag.id/assigned-review-email
                :feature-flag-model/description "Enable email notifications for assigned reviews"
                :feature-flag-model/jira-issue  "WIN-3190"
                :feature-flag-model/epic        "WIN-3191"}
               {:feature-flag/id                :feature-flag.id/configure-gis-source
                :feature-flag-model/description "Enable GIS source configuration"
                :feature-flag-model/jira-issue  "WIN-3655"
                :feature-flag-model/epic        "WIN-3863"}
               {:feature-flag/id                :feature-flag.id/comply-date-restriction
                :feature-flag-model/description "Non-working days cannot be selected in deficiency comply by date"
                :feature-flag-model/jira-issue  "WIN-4172"
                :feature-flag-model/epic        "WIN-4106"}
               {:feature-flag/id                :feature-flag.id/dispatch-assign
                :feature-flag-model/description "Enable Assign inspector functionality in dispatching"
                :feature-flag-model/jira-issue  "WIN-4164"
                :feature-flag-model/epic        "WIN-4106"}
               {:feature-flag/id                :feature-flag.id/inspection-type-table-v2
                :feature-flag-model/description "Upgrade inspection type table to modern responsive table."
                :feature-flag-model/jira-issue  "WIN-4176"
                :feature-flag-model/epic        "WIN-3904"}
               {:feature-flag/id                :feature-flag.id/reporting-content-packages
                :feature-flag-model/description "Enable reporting user and manager to create content packages"
                :feature-flag-model/jira-issue  "WIN-4013"
                :feature-flag-model/epic        "WIN-4000"}
               {:feature-flag/id                :feature-flag.id/public-works-location-enhancements
                :feature-flag-model/description "Public Works Phase 2 GIS Enhancements - using ROW layers"
                :feature-flag-model/jira-issue  "WIN-4165"
                :feature-flag-model/epic        "WIN-4165"}
               {:feature-flag/id                :feature-flag.id/add-planning-dates-to-import
                :feature-flag-model/description "Add specific date fields to planning data import"
                :feature-flag-model/jira-issue  "WIN-4014"
                :feature-flag-model/epic        "WIN-3893"}
               {:feature-flag/id                :feature-flag.id/group-config-export-extensions
                :feature-flag-model/description "Extend group config export/import scope to include group-tied entities and non-group-admin features"
                :feature-flag-model/jira-issue  "WIN-5070"
                :feature-flag-model/epic        "WIN-3733"}
               {:feature-flag/id                :feature-flag.id/rich-text-in-applicant-review-comments
                :feature-flag-model/description "Show rich text in applicant review table comments"
                :feature-flag-model/jira-issue  "WIN-4081"
                :feature-flag-model/epic        "WIN-4078"}
               {:feature-flag/id                :feature-flag.id/property-attributes-gis-link
                :feature-flag-model/description "Link GIS attributes to property attributes"
                :feature-flag-model/jira-issue  "WIN-4058"
                :feature-flag-model/epic        "WIN-3637"}
               {:feature-flag/id                :feature-flag.id/multiple-inspection-bookings
                :feature-flag-model/description "Enable requesting multiple dates for an inspection"
                :feature-flag-model/jira-issue  "WIN-4107"
                :feature-flag-model/epic        "WIN-4106"}
               {:feature-flag/id                :feature-flag.id/property-relations-view
                :feature-flag-model/description "Enable property relations view"
                :feature-flag-model/jira-issue  "WIN-4124"
                :feature-flag-model/epic        "WIN-3863"}
               {:feature-flag/id                :feature-flag.id/property-attachments
                :feature-flag-model/description "Add ability to add attachments directly to properties"
                :feature-flag-model/jira-issue  "WIN-4068"
                :feature-flag-model/epic        "WIN-3638"}
               {:feature-flag/id                :feature-flag.id/display-vector-layers-on-map
                :feature-flag-model/description "Display vector layers in map views"
                :feature-flag-model/jira-issue  "WIN-4353"
                :feature-flag-model/epic        "WIN-4165"}
               {:feature-flag/id                :feature-flag.id/assignee-receive-email-on-circulation-request
                :feature-flag-model/description "Assignee receives email when circulation request is completed"
                :feature-flag-model/jira-issue  "WIN-4231"
                :feature-flag-model/epic        "WIN-3708"}
               {:feature-flag/id                :feature-flag.id/bill-invoice-numbering
                :feature-flag-model/description "Enable automatic bill and invoice numbering"
                :feature-flag-model/jira-issue  "WIN-4101"
                :feature-flag-model/epic        "WIN-3973"}
               {:feature-flag/id                :feature-flag.id/add-cheque-number
                :feature-flag-model/description "Add cheque number field to payment details"
                :feature-flag-model/jira-issue  "WIN-4145"
                :feature-flag-model/epic        "WIN-3973"}
               {:feature-flag/id                :feature-flag.id/multi-select-issued-to
                :feature-flag-model/description "Enable multi-select for inspection order issued to field"
                :feature-flag-model/jira-issue  "WIN-4110"
                :feature-flag-model/epic        "WIN-3912"}
               {:feature-flag/id                :feature-flag.id/inspection-restriction
                :feature-flag-model/description "Enable inspection restriction settings"
                :feature-flag-model/jira-issue  "WIN-4113"
                :feature-flag-model/epic        "WIN-3905"}
               {:feature-flag/id                :feature-flag.id/inspection-restriction-mobile
                :feature-flag-model/description "Enable inspection restriction settings also to groups with mobile app enabled."
                :feature-flag-model/jira-issue  "WIN-4113"
                :feature-flag-model/epic        "WIN-3905"
                :feature-flag-model/depends-on  #{:feature-flag.id/inspection-restriction}}
               {:feature-flag/id                :feature-flag.id/enhanced-order-templates
                :feature-flag-model/description "Enable comply-by-date column for order deficiencies"
                :feature-flag-model/jira-issue  "WIN-4111"
                :feature-flag-model/epic        "WIN-3912"}
               {:feature-flag/id                :feature-flag.id/deficiency-comply-by-bookings
                :feature-flag-model/description "Enable booking inspections based on deficiency comply by date"
                :feature-flag-model/jira-issue  "WIN-4108"
                :feature-flag-model/epic        "WIN-4106"
                :feature-flag-model/depends-on  #{:feature-flag.id/comply-date-restriction}}
               {:feature-flag/id                :feature-flag.id/formula-variables
                :feature-flag-model/description "Formula-based calculated variables for fee management"
                :feature-flag-model/jira-issue  "WIN-4132"
                :feature-flag-model/epic        "WIN-3973"}
               {:feature-flag/id                :feature-flag.id/bill-reversal-methods
                :feature-flag-model/description "Bill reversal methods for refunds, NSF charges, and voiding bills"
                :feature-flag-model/jira-issue  "WIN-4155"
                :feature-flag-model/epic        "WIN-3973"}
               {:feature-flag/id                :feature-flag.id/requested-date-on-bookings
                :feature-flag-model/description "Show the recorded date of when an inspection was requested on the Dispatch queue and Inspection UI"
                :feature-flag-model/jira-issue  "WIN-4174"
                :feature-flag-model/epic        "WIN-4056"}
               {:feature-flag/id                :feature-flag.id/prevent-request-when-unpaid
                :feature-flag-model/description "Request for inspection becomes unavailable if there is a bill to be paid"
                :feature-flag-model/jira-issue  "WIN-4319"
                :feature-flag-model/epic        "WIN-4207"}
               {:feature-flag/id                :feature-flag.id/complaints-wo-property
                :feature-flag-model/description "Allow creating complaints and cases without linking to a property"
                :feature-flag-model/jira-issue  "WIN-4439"
                :feature-flag-model/epic        "WIN-4377"}
               {:feature-flag/id                :feature-flag.id/inspection-stakeholder-notifications
                :feature-flag-model/description "Email Notification to Stakeholder when inspection is confirmed or visit completed"
                :feature-flag-model/jira-issue  "WIN-4323"
                :feature-flag-model/epic        "WIN-4207"}
               {:feature-flag/id                :feature-flag.id/payment-emails
                :feature-flag-model/description "Enable payment notification emails"
                :feature-flag-model/jira-issue  "WIN-3888"
                :feature-flag-model/epic        "WIN-3973"}
               {:feature-flag/id                :feature-flag.id/deficiency-photo-linking
                :feature-flag-model/description "Inspection deficiency and photo linking"
                :feature-flag-model/jira-issue  "WIN-4186"
                :feature-flag-model/epic        "WIN-3700"
                :feature-flag-model/depends-on  #{:feature-flag.id/deficiency-photo-numbering}}
               {:feature-flag/id                :feature-flag.id/pdf-validation-only-by-default
                :feature-flag-model/description "PDF validation only by default (no PDF/A conversion)"
                :feature-flag-model/jira-issue  "WIN-4115"
                :feature-flag-model/epic        "WIN-4115"}
               {:feature-flag/id                :feature-flag.id/enable-case-reporting-additional-categories
                :feature-flag-model/description "Enable additional-categories case reporting output column"
                :feature-flag-model/jira-issue  "WIN-4438"
                :feature-flag-model/depends-on  #{:feature-flag.id/allow-multiple-categories-to-a-case}}
               {:feature-flag/id                :feature-flag.id/ce-and-pwo-translations
                :feature-flag-model/description "Enable new translations common to code enforcement and work orders"
                :feature-flag-model/jira-issue  "WIN-4386"
                :feature-flag-model/epic        "WIN-4378"}
               {:feature-flag/id                :feature-flag.id/comply-by-date-notifications
                :feature-flag-model/description "Enable email notifications for upcoming comply by date deficiencies"
                :feature-flag-model/jira-issue  "WIN-4109"
                :feature-flag-model/epic        "WIN-4106"}
               {:feature-flag/id                :feature-flag.id/create-workspace-with-manual-location
                :feature-flag-model/description "Enables creating a workspace with a manually entered location"
                :feature-flag-model/jira-issue  "WIN-4520"
                :feature-flag-model/epic        "WIN-4165"}
               {:feature-flag/id                :feature-flag.id/reporting-property-attributes
                :feature-flag-model/description "Enable property attributes in reporting"
                :feature-flag-model/jira-issue  "WIN-4197"
                :feature-flag-model/epic        "WIN-4051"}
               {:feature-flag/id                :feature-flag.id/domain-group-payment-reporting
                :feature-flag-model/description "Enable payment reporting for domain groups"
                :feature-flag-model/jira-issue  "WIN-4541"
                :feature-flag-model/epic        "WIN-4428"}
               {:feature-flag/id                :feature-flag.id/let-assigned-payer-pay-the-bill
                :feature-flag-model/description "Let assigned payer get informed and pay the bill"
                :feature-flag-model/jira-issue  "WIN-3880"
                :feature-flag-model/epic        "WIN-3973"}
               {:feature-flag/id                :feature-flag.id/complaints-priority
                :feature-flag-model/description "Using complaints severity levels"
                :feature-flag-model/jira-issue  "WIN-4383"
                :feature-flag-model/epic        "WIN-4377"}
               {:feature-flag/id                :feature-flag.id/process-acceptable-mime-types-in-zip
                :feature-flag-model/description "Process acceptable mime types in ZIP objects"
                :feature-flag-model/jira-issue  "WIN-4274"
                :feature-flag-model/epic        "WIN-4272"}
               {:feature-flag/id                :feature-flag.id/inspection-dependencies
                :feature-flag-model/description "Enable inspection dependencies"
                :feature-flag-model/jira-issue  "WIN-4170"
                :feature-flag-model/epic        "WIN-3904"}
               {:feature-flag/id                :feature-flag.id/complaint-submission-configuration
                :feature-flag-model/description "PLF - Complaint Submission Configuration"
                :feature-flag-model/jira-issue  "WIN-4382"
                :feature-flag-model/epic        "WIN-4377"}
               {:feature-flag/id                :feature-flag.id/property-attribute-audit-log
                :feature-flag-model/description "Enable property attribute and attachment audit log"
                :feature-flag-model/jira-issue  "WIN-3927"
                :feature-flag-model/epic        "WIN-4051"}
               {:feature-flag/id                :feature-flag.id/bonds
                :feature-flag-model/description "Enable top level bond features"
                :feature-flag-model/jira-issue  "WIN-3704"
                :feature-flag-model/epic        "WIN-3704"}
               {:feature-flag/id                :feature-flag.id/create-property-from-workspace
                :feature-flag-model/description "Allow property managers to create a property from PW application"
                :feature-flag-model/jira-issue  "WIN-4590"}
               {:feature-flag/id                :feature-flag.id/bill-reverse
                :feature-flag-model/description "Enable reversing the bill and invoice trail"
                :feature-flag-model/jira-issue  "WIN-4082"
                :feature-flag-model/epic        "WIN-3973"}
               {:feature-flag/id                :feature-flag.id/revised-payments-report
                :feature-flag-model/description "Revised fees and payments report / online payments - total/itemized"
                :feature-flag-model/jira-issue  "WIN-4090"
                :feature-flag-model/epic        "WIN-3973"}
               {:feature-flag/id                :feature-flag.id/hide-deprecated-payments-report
                :feature-flag-model/description "Hide deprecated version of payments report"
                :feature-flag-model/jira-issue  "WIN-4090"
                :feature-flag-model/epic        "WIN-3973"}
               {:feature-flag/id                :feature-flag.id/enhanced-template-configuration
                :feature-flag-model/description "Enhanced template configuration for inspection report, occupancy report and letter"
                :feature-flag-model/jira-issue  "WIN-4187"
                :feature-flag-model/epic        "WIN-3700"
                :feature-flag-model/depends-on  #{:feature-flag.id/deficiency-photo-linking}}
               {:feature-flag/id                :feature-flag.id/property-attribute-api
                :feature-flag-model/description "Property attribute API"
                :feature-flag-model/jira-issue  "WIN-4199"
                :feature-flag-model/epic        "WIN-4051"}
               {:feature-flag/id                :feature-flag.id/mass-delete-empty-properties
                :feature-flag-model/description "Allow property managers to mass delete empty property records"
                :feature-flag-model/jira-issue  "WIN-3931"
                :feature-flag-model/epic        "WIN-4051"}
               {:feature-flag/id                :feature-flag.id/checklist-configuration-v2
                :feature-flag-model/description "Enhanced checklist configuration"
                :feature-flag-model/jira-issue  "WIN-4206"
                :feature-flag-model/epic        "WIN-4206"}
               {:feature-flag/id                :feature-flag.id/checklist-configuration-v2-mobile
                :feature-flag-model/description "Enhanced checklist configuration for mobile app"
                :feature-flag-model/jira-issue  "WIN-4723"
                :feature-flag-model/epic        "WIN-4206"
                :feature-flag-model/depends-on  #{:feature-flag.id/checklist-configuration-v2}}
               {:feature-flag/id                :feature-flag.id/complaint-sign-off-may-be-required
                :feature-flag-model/description "Complaint might be signed off"
                :feature-flag-model/jira-issue  "WIN-4300"
                :feature-flag-model/epic        "WIN-4377"}
               {:feature-flag/id                :feature-flag.id/outstanding-bill-close-inspection
                :feature-flag-model/description "Allow configuring visit results to require bill payment before closing"
                :feature-flag-model/jira-issue  "WIN-4320"
                :feature-flag-model/epic        "WIN-4207"}
               {:feature-flag/id                :feature-flag.id/complaint-permit-type-selection
                :feature-flag-model/description "Filter complaint categories by selected department"
                :feature-flag-model/jira-issue  "WIN-4642"
                :feature-flag-model/epic        "WIN-4377"}
               {:feature-flag/id                :feature-flag.id/sub-review-modified-ts
                :feature-flag-model/description "Modified timestamp for reviews"
                :feature-flag-model/jira-issue  "TT-25697"}
               {:feature-flag/id                :feature-flag.id/public-work-orders-public-map
                :feature-flag-model/description "Public map for public work orders"
                :feature-flag-model/jira-issue  "WIN-4399"
                :feature-flag-model/epic        "WIN-4380"}
               {:feature-flag/id                :feature-flag.id/inspection-ai-assistant-group-admin-toggle
                :feature-flag-model/description "Allow group admin to toggle inspection AI assistant feature"
                :feature-flag-model/jira-issue  "WIN-4692"
                :feature-flag-model/epic        "WIN-4284"}
               {:feature-flag/id                :feature-flag.id/set-bill-unpaid
                :feature-flag-model/description "Allow setting a bill as unpaid if confirmation was not received from payment provider"
                :feature-flag-model/jira-issue  "TT-25589"}
               {:feature-flag/id                :feature-flag.id/inspection-priority
                :feature-flag-model/description "Enable inspection/task priority"
                :feature-flag-model/jira-issue  "WIN-4379"
                :feature-flag-model/epic        "WIN-4379"}
               {:feature-flag/id                :feature-flag.id/complaints-main-menu-enhancements
                :feature-flag-model/description "Hide map and show product filter in complaints main menu"
                :feature-flag-model/jira-issue  "WIN-4302"
                :feature-flag-model/epic        "WIN-4377"}
               {:feature-flag/id                :feature-flag.id/inspection-created-due-date
                :feature-flag-model/description "Display inspection created and due date on inspection visit page"
                :feature-flag-model/jira-issue  "WIN-4696"
                :feature-flag-model/epic        "WIN-4683"}
               {:feature-flag/id                :feature-flag.id/wizard-ai
                :feature-flag-model/description "Wizard AI prototype"}
               {:feature-flag/id                :feature-flag.id/add-complainant-as-related-party
                :feature-flag-model/description "Add flag 'Add Complainant as Related Party' to case creation panel"
                :feature-flag-model/jira-issue  "WIN-4632"
                :feature-flag-model/epic        "WIN-4377"}
               {:feature-flag/id                :feature-flag.id/pwo-notification-flags
                :feature-flag-model/description "Add flags to user profile for receiving work/task order notifications"
                :feature-flag-model/jira-issue  "WIN-4744"
                :feature-flag-model/epic        "WIN-4380"}
               {:feature-flag/id                :feature-flag.id/domain-level-meetings
                :feature-flag-model/description "Enable domain level meetings and related meeting improvements"
                :feature-flag-model/jira-issue  "WIN-4358"
                :feature-flag-model/epic        "WIN-4358"}
               {:feature-flag/id                :feature-flag.id/reassign-from-modify-request-modal
                :feature-flag-model/description "Enable inspection reassigning from modify request modal"
                :feature-flag-model/jira-issue  "WIN-4411"
                :feature-flag-model/epic        "WIN-4379"}
               {:feature-flag/id                :feature-flag.id/inspection-ui-improvements
                :feature-flag-model/description "Move add buttons to options menu and hide empty sections in inspection views"
                :feature-flag-model/jira-issue  "WIN-4205"
                :feature-flag-model/epic        "WIN-4776"}
               {:feature-flag/id                :feature-flag.id/public-notice-in-execution
                :feature-flag-model/description "Show public notice in execution phase for other permit types (P, LU) than PWO"
                :feature-flag-model/jira-issue  "WIN-4871"
                :feature-flag-model/epic        "WIN-4378"}
               {:feature-flag/id                :feature-flag.id/superdomain-can-manage-forms
                :feature-flag-model/description "Forms can be created and inherited from Shared service groups. Also enables review reports for shared services groups."
                :feature-flag-model/jira-issue  "WIN-4556"
                :feature-flag-model/epic        "WIN-4534"}
               {:feature-flag/id                :feature-flag.id/dashboard-filterable-multi-select
                :feature-flag-model/description "Enable filterable multi-select components on dashboard search"
                :feature-flag-model/jira-issue  "WIN-4915"
                :feature-flag-model/epic        "WIN-4775"}
               {:feature-flag/id                :feature-flag.id/licence-and-contractor-registration-public-records
                :feature-flag-model/description "Public records for licences and contractor registrations"}
               {:feature-flag/id                :feature-flag.id/deficiency-quick-pick-list
                :feature-flag-model/description "Enable quick pick list for inspection deficiencies"
                :feature-flag-model/jira-issue  "WIN-4950"
                :feature-flag-model/epic        "WIN-4688"}
               {:feature-flag/id                :feature-flag.id/superdomain-can-manage-templates
                :feature-flag-model/description "Templates can be created and inherited from shared services group"
                :feature-flag-model/jira-issue  "WIN-4557"
                :feature-flag-model/epic        "WIN-4534"}
               {:feature-flag/id                :feature-flag.id/skip-config-creation-for-new-groups
                :feature-flag-model/description "Skip config creation for new groups in production for easier importing"
                :feature-flag-model/jira-issue  "WIN-5000"
                :feature-flag-model/epic        "WIN-4754"}
               {:feature-flag/id                :feature-flag.id/superdomain-working-day-calendar
                :feature-flag-model/description "Allow shared services groups to use working day calendar and groups under them inherit it. Also moves inspection time slots to the working day calendar."
                :feature-flag-model/jira-issue  "WIN-4925"
                :feature-flag-model/epic        "WIN-4534"}
               {:feature-flag/id                :feature-flag.id/approver-group-creation-for-domains
                :feature-flag-model/description "Enable domain group admins to create approver groups"
                :feature-flag-model/jira-issue  "WIN-3585"
                :feature-flag-model/epic        "WIN-5599"}
               {:feature-flag/id                :feature-flag.id/enable-condition-template-snippets
                :feature-flag-model/description "Enable condition snippets in letters, orders, and inspection reports"
                :feature-flag-model/jira-issue  "WIN-4233"
                :feature-flag-model/epic        "WIN-4754"}
               {:feature-flag/id                :feature-flag.id/custom-branding
                :feature-flag-model/description "Enable website branding for all municipalities (overrides per-group custom-branding flag)"
                :feature-flag-model/jira-issue  "WIN-5034"
                :feature-flag-model/epic        "WIN-4775"}
               {:feature-flag/id                :feature-flag.id/bounced-circulation-notification
                :feature-flag-model/description "Send email notifications when a circulation request bounces back"
                :feature-flag-model/jira-issue  "WIN-3828"
                :feature-flag-model/epic        "WIN-5599"}
               {:feature-flag/id                :feature-flag.id/ai-chat
                :feature-flag-model/description "AI chat / NoVa"
                :feature-flag-model/jira-issue  "WIN-4818"
                :feature-flag-model/epic        "WIN-4818"}
               {:feature-flag/id                :feature-flag.id/ai-chat-context-aware
                :feature-flag-model/description "Context-aware AI chat: page context, workspace tools, and enhanced system prompt"
                :feature-flag-model/jira-issue  "WIN-5286"
                :feature-flag-model/epic        "WIN-4818"
                :feature-flag-model/depends-on  #{:feature-flag.id/ai-chat}}
               {:feature-flag/id                :feature-flag.id/ai-chat-guide-context
                :feature-flag-model/description "Guide-context AI chat: page guide elements, page descriptions, and hover highlighting"
                :feature-flag-model/depends-on  #{:feature-flag.id/ai-chat-context-aware}
                :feature-flag-model/jira-issue  "WIN-5448"
                :feature-flag-model/epic        "WIN-4780"}
               {:feature-flag/id                :feature-flag.id/condition-due-dates
                :feature-flag-model/description "Enable condition due dates and overdue status"
                :feature-flag-model/jira-issue  "WIN-4236"
                :feature-flag-model/epic        "WIN-5599"}
               {:feature-flag/id                :feature-flag.id/condition-due-date-reminders
                :feature-flag-model/description "Enable email reminders for upcoming and overdue condition due dates"
                :feature-flag-model/jira-issue  "WIN-4237"
                :feature-flag-model/epic        "WIN-4221"
                :feature-flag-model/depends-on  #{:feature-flag.id/condition-due-dates}}
               {:feature-flag/id                :feature-flag.id/property-db-units
                :feature-flag-model/description "Enable units and their associated features for property database"
                :feature-flag-model/jira-issue  "WIN-2922"
                :feature-flag-model/epic        "WIN-2922"}
               {:feature-flag/id                :feature-flag.id/property-verification
                :feature-flag-model/description "Enable unverified properties management"
                :feature-flag-model/jira-issue  "WIN-4983"
                :feature-flag-model/epic        "WIN-2922"}
               {:feature-flag/id                :feature-flag.id/icc-cloudcheck-prototype
                :feature-flag-model/description "CloudCheck workspace prototype"
                :feature-flag-model/jira-issue  "WIN-5805"
                :feature-flag-model/epic        "WIN-5739"}
               {:feature-flag/id                :feature-flag.id/application-wizard
                :feature-flag-model/description "Guided application wizard flow"
                :feature-flag-model/jira-issue  "WIN-4777"
                :feature-flag-model/epic        "WIN-4777"}
               {:feature-flag/id                :feature-flag.id/wizard-ai-precheck
                :feature-flag-model/description "AI pre-check in the application wizard summary"
                :feature-flag-model/jira-issue  "WIN-5756"
                :feature-flag-model/epic        "WIN-4777"
                :feature-flag-model/depends-on  #{:feature-flag.id/application-wizard}}
               {:feature-flag/id                :feature-flag.id/form-confirmation
                :feature-flag-model/description "Require applicants to confirm AI-updated form data before submit"
                :feature-flag-model/jira-issue  "WIN-5455"
                :feature-flag-model/epic        "WIN-4777"}
               {:feature-flag/id                :feature-flag.id/bluebeam-end-markup-unreviewed-acceptance
                :feature-flag-model/description "Bluebeam after end mark-up, ensure attachment acceptance value is unreviewed"
                :feature-flag-model/jira-issue  "WIN-5237"
                :feature-flag-model/epic        "WIN-4329"}
               {:feature-flag/id                :feature-flag.id/version-specific-conversion-info
                :feature-flag-model/description "Enable showing version-specific conversion info in PDF validation results"
                :feature-flag-model/jira-issue  "WIN-5211"
                :feature-flag-model/epic        "WIN-4329"}
               {:feature-flag/id                :feature-flag.id/express-issuance
                :feature-flag-model/description "Express issuance"
                :feature-flag-model/jira-issue  "WIN-4637"
                :feature-flag-model/epic        "WIN-4637"}
               {:feature-flag/id                :feature-flag.id/freshdesk-tickets
                :feature-flag-model/description "Enable Freshdesk ticket creation from AI chat"
                :feature-flag-model/depends-on  #{:feature-flag.id/ai-chat-context-aware}
                :feature-flag-model/jira-issue  "WIN-5182"
                :feature-flag-model/epic        "WIN-4818"}
               {:feature-flag/id                :feature-flag.id/proxy-all-arcgis-map-layers
                :feature-flag-model/description "Route authenticated ArcGIS overlay layers (gis-map-config, gis-search-config) through map-proxy-v2 so customer ArcGIS tokens are never sent to the browser. Esri basemap tiles are unaffected."
                :feature-flag-model/jira-issue  "WIN-5328"}
               {:feature-flag/id                :feature-flag.id/inspection-ai-assistant
                :feature-flag-model/description "Enable inspection AI assistant"
                :feature-flag-model/jira-issue  "WIN-5369"
                :feature-flag-model/epic        "WIN-4284"}
               {:feature-flag/id                :feature-flag.id/inspection-ai-assistant-quebec
                :feature-flag-model/description "Enable inspection AI assistant for Quebec (do not turn on)"
                :feature-flag-model/jira-issue  "WIN-5369"
                :feature-flag-model/epic        "WIN-4284"}
               {:feature-flag/id                :feature-flag.id/form-editor-tables
                :feature-flag-model/description "Allow adding table fields in the form schema editor"
                :feature-flag-model/jira-issue  "WIN-5379"}
               {:feature-flag/id                :feature-flag.id/review-due-date
                :feature-flag-model/description "Review due dates"
                :feature-flag-model/jira-issue  "WIN-4137"
                :feature-flag-model/epic        "WIN-4585"}
               {:feature-flag/id                :feature-flag.id/signatures-in-execution-forms
                :feature-flag-model/description "Allow signature fields in work/fulfillment phase forms"
                :feature-flag-model/jira-issue  "WIN-5419"}
               {:feature-flag/id                :feature-flag.id/upcoming-changes-modal
                :feature-flag-model/description "Show the Upcoming Changes modal and header button"
                :feature-flag-model/jira-issue  "WIN-5773"
                :feature-flag-model/epic        "WIN-4777"}
               {:feature-flag/id                :feature-flag.id/system-email-templates
                :feature-flag-model/description "Enable system email template administration"
                :feature-flag-model/jira-issue  "WIN-4687"
                :feature-flag-model/epic        "WIN-4684"}
               {:feature-flag/id                :feature-flag.id/pre-issuance-inspections
                :feature-flag-model/description "Enable inspections in pre-issuance phases and stages"
                :feature-flag-model/jira-issue  "WIN-3802"
                :feature-flag-model/epic        "WIN-3802"}
               {:feature-flag/id                :feature-flag.id/reviews-in-submitted
                :feature-flag-model/description "Allow Reviews to be conducted in the Submitted state"
                :feature-flag-model/jira-issue  "WIN-4044"
                :feature-flag-model/epic        "WIN-4585"}
               {:feature-flag/id                :feature-flag.id/wizard-estimate-costs
                :feature-flag-model/description "Show estimated costs in the application wizard summary"}
               {:feature-flag/id                :feature-flag.id/business-parties-all-permit-types
                :feature-flag-model/description "Allow businesses to be added as parties and chosen as initial applicants in all permit types (licences and contractor registrations always allow them)"
                :feature-flag-model/jira-issue  "WIN-5611"
                :feature-flag-model/epic        "WIN-4777"}
               {:feature-flag/id                :feature-flag.id/nova-form-filler
                :feature-flag-model/description "Allow NoVa to read and interactively fill/update application wizard form fields via chat"
                :feature-flag-model/depends-on  #{:feature-flag.id/ai-chat-context-aware}
                :feature-flag-model/jira-issue  "WIN-4780"
                :feature-flag-model/epic        "WIN-4780"}
               {:feature-flag/id                :feature-flag.id/changes-made-in-form-navigation-bar
                :feature-flag-model/description "Add the Changes made button to the form navigation bar"
                :feature-flag-model/jira-issue  "WIN-4079"
                :feature-flag-model/epic        "WIN-4585"}
               {:feature-flag/id                :feature-flag.id/standard-texts-import
                :feature-flag-model/description "Import/export buttons for standard texts"
                :feature-flag-model/jira-issue  "WIN-5520"
                :feature-flag-model/epic        "WIN-5516"
                :feature-flag-model/depends-on  #{:feature-flag.id/standard-texts-admin-view}}
               {:feature-flag/id                :feature-flag.id/service-hub-panel
                :feature-flag-model/description "Service Hub side panel"
                :feature-flag-model/jira-issue  "WIN-4251"
                :feature-flag-model/epic        "WIN-4256"}
               {:feature-flag/id                :feature-flag.id/workspace-search-updates
                :feature-flag-model/description "Workspace search & tools update"
                :feature-flag-model/jira-issue  "WIN-4256"
                :feature-flag-model/epic        "WIN-4256"}
               {:feature-flag/id                :feature-flag.id/inspection-labels
                :feature-flag-model/description "Enable predefined label groups for inspections with export/import functionality"
                :feature-flag-model/jira-issue  "WIN-4506"
                :feature-flag-model/epic        "WIN-4208"}
               {:feature-flag/id                :feature-flag.id/inspection-labels-mobile
                :feature-flag-model/description "Enable inspection labels feature for groups with mobile app enabled"
                :feature-flag-model/depends-on  #{:feature-flag.id/inspection-labels}
                :feature-flag-model/jira-issue  "WIN-4506"
                :feature-flag-model/epic        "WIN-4208"}
               {:feature-flag/id                :feature-flag.id/show-revert-to-submitted
                :feature-flag-model/description "Show the revert to Submitted action in the UI"
                :feature-flag-model/jira-issue  "WIN-4149"
                :feature-flag-model/epic        "WIN-4585"}
               {:feature-flag/id                :feature-flag.id/review-assignment-groups
                :feature-flag-model/description "Assign reviews to a group of reviewers instead of an individual"
                :feature-flag-model/jira-issue  "WIN-5116"
                :feature-flag-model/epic        "WIN-4585"}
               {:feature-flag/id                :feature-flag.id/permit-pdf-table-form-fields
                :feature-flag-model/description "Allow table form fields to render on permit PDFs"
                :feature-flag-model/jira-issue  "WIN-5710"
                :feature-flag-model/epic        "WIN-5516"}
               {:feature-flag/id                :feature-flag.id/enforcement-inspection-report-checklists
                :feature-flag-model/description "Add checklist snippet to enforcement inspection report template"
                :feature-flag-model/jira-issue  "WIN-5715"
                :feature-flag-model/depends-on  #{:feature-flag.id/checklist-configuration-v2}}
               {:feature-flag/id                :feature-flag.id/batch-group-creation
                :feature-flag-model/description "Batch group creation"
                :feature-flag-model/jira-issue  "WIN-4836"
                :feature-flag-model/epic        "WIN-4534"}
               {:feature-flag/id                :feature-flag.id/lower-tier-municipality-workspace-filter
                :feature-flag-model/description "Enable sorting of applications by municipality"
                :feature-flag-model/jira-issue  "WIN-5094"
                :feature-flag-model/epic        "WIN-4221"}
               {:feature-flag/id                :feature-flag.id/application-deny-option
                :feature-flag-model/jira-issue  "WIN-5085"
                :feature-flag-model/epic        "WIN-4221"
                :feature-flag-model/description "Add deny option to all application process flows"}
               {:feature-flag/id                :feature-flag.id/appeal-in-more-steps
                :feature-flag-model/description "Allow marking an application Appealed from more steps."
                :feature-flag-model/jira-issue  "WIN-5082"
                :feature-flag-model/epic        "WIN-4221"}
               {:feature-flag/id                :feature-flag.id/property-mass-delete-domain-group-properties
                :feature-flag-model/description "Allow property managers to mass delete empty property records in a domain group"
                :feature-flag-model/jira-issue  "WIN-5719"
                :feature-flag-model/epic        "WIN-4714"}
               {:feature-flag/id                :feature-flag.id/review-history
                :feature-flag-model/description "Show review comments and item level comments grouped by review round"
                :feature-flag-model/jira-issue  "WIN-4587"
                :feature-flag-model/epic        "WIN-4585"}
               {:feature-flag/id                :feature-flag.id/property-data-integrity-tooling
                :feature-flag-model/description "Enable property data integrity tooling (Property DB v3)"
                :feature-flag-model/jira-issue  "WIN-4714"
                :feature-flag-model/epic        "WIN-4714"}
               {:feature-flag/id                :feature-flag.id/planning-lapsed-status
                :feature-flag-model/description "Allow marking a Planning fulfillment as Lapsed."
                :feature-flag-model/jira-issue  "WIN-5117"
                :feature-flag-model/epic        "WIN-4221"}
               {:feature-flag/id                :feature-flag.id/back-to-submitted-enhancements
                :feature-flag-model/description "Reason and processing time reset when going back to submitted. Revert workspace to submitted replaces Back to submitted action."
                :feature-flag-model/jira-issue  "WIN-5087"
                :feature-flag-model/epic        "WIN-4221"}
               {:feature-flag/id                :feature-flag.id/shared-form-editor-link
                :feature-flag-model/description "Link inherited forms to their Shared Services group editor"
                :feature-flag-model/jira-issue  "WIN-5794"
                :feature-flag-model/epic        "WIN-5115"
                :feature-flag-model/depends-on  #{:feature-flag.id/superdomain-can-manage-forms}}
               {:feature-flag/id                :feature-flag.id/final-review
                :feature-flag-model/description "Final Review: hold and bundle change requests until final review completion"
                :feature-flag-model/jira-issue  "WIN-4222"
                :feature-flag-model/epic        "WIN-4585"}
               {:feature-flag/id                :feature-flag.id/start-new-review-round
                :feature-flag-model/jira-issue  "WIN-5063"
                :feature-flag-model/epic        "WIN-4585"
                :feature-flag-model/description "Authority can start a new review round before the applicant marks changes as done"}
               {:feature-flag/id                :feature-flag.id/planning-condition-attachments
                :feature-flag-model/description "Upload attachments to planning conditions"
                :feature-flag-model/jira-issue  "WIN-4235"
                :feature-flag-model/epic        "WIN-4221"}
               {:feature-flag/id                :feature-flag.id/attachment-list-v2
                :feature-flag-model/jira-issue  "WIN-5602"
                :feature-flag-model/epic        "WIN-5602"
                :feature-flag-model/description "Use the sortable attachment list"}
               {:feature-flag/id                :feature-flag.id/full-screen-messages
                :feature-flag-model/description "Enable the full screen messages view"
                :feature-flag-model/jira-issue  "WIN-5810"
                :feature-flag-model/epic        "WIN-4246"}
               {:feature-flag/id                :feature-flag.id/pdf-markup-tool
                :feature-flag-model/jira-issue  "WIN-4647"
                :feature-flag-model/epic        "WIN-4647"
                :feature-flag-model/description "Internal PDF markup tool prototype"}
               {:feature-flag/id                :feature-flag.id/renewal-copy-internal-notes
                :feature-flag-model/description "Copy internal notes when renewing licences and contractor registrations"
                :feature-flag-model/jira-issue  "WIN-4966"
                :feature-flag-model/epic        "WIN-4660"}
               {:feature-flag/id                :feature-flag.id/review-config-removal
                :feature-flag-model/description "Allow administrators to remove disabled review configurations"
                :feature-flag-model/jira-issue  "WIN-4486"
                :feature-flag-model/epic        "WIN-4585"}
               {:feature-flag/id                :feature-flag.id/saved-searches
                :feature-flag-model/description "Saved searches"
                :feature-flag-model/depends-on  #{:feature-flag.id/workspace-search-updates}
                :feature-flag-model/jira-issue  "WIN-5809"
                :feature-flag-model/epic        "WIN-4256"}
               {:feature-flag/id                :feature-flag.id/renewal-box
                :feature-flag-model/jira-issue  "WIN-4974"
                :feature-flag-model/epic        "WIN-4660"
                :feature-flag-model/description "Show renewal boxes on issued licence and contractor registration views"}
               {:feature-flag/id                :feature-flag.id/allow-reject-refuse-deny-in-more-steps
                :feature-flag-model/jira-issue  "WIN-5086"
                :feature-flag-model/epic        "WIN-4221"
                :feature-flag-model/description "Allow reject/refuse/deny in more steps"
                :feature-flag-model/depends-on  #{:feature-flag.id/application-deny-option}}
               {:feature-flag/id                :feature-flag.id/municipal-case-id-year-only-on-renewal?
                :feature-flag-model/description "Allow licence and case id renewal to be done using only the licence year"
                :feature-flag-model/jira-issue  "WIN-4663"
                :feature-flag-model/epic        "WIN-4660"}
               {:feature-flag/id                :feature-flag.id/prevent-incompatible-process-change
                :feature-flag-model/description "Prevent progressed workspaces from changing to a different process flow"
                :feature-flag-model/jira-issue  "TT-26937"}
               {:feature-flag/id                :feature-flag.id/applicant-review-history
                :feature-flag-model/description "Add option to show detailed review history to applicants"
                :feature-flag-model/jira-issue  "WIN-5997"
                :feature-flag-model/epic        "WIN-4585"
                :feature-flag-model/depends-on  #{:feature-flag.id/review-history}}
               {:feature-flag/id                :feature-flag.id/renewal-bill-due-date
                :feature-flag-model/jira-issue  "WIN-4669"
                :feature-flag-model/description "Renewal bills with a fee that allows penalties are due on the L/CR expiry date. Penalty and interest accrue on the earliest bill of the renewal."
                :feature-flag-model/depends-on  #{:feature-flag.id/penalty-and-interest}}
               {:feature-flag/id                :feature-flag.id/new-tenant-management
                :feature-flag-model/description "Show the new tenant management view in the admin menu"
                :feature-flag-model/jira-issue  "WIN-5734"
                :feature-flag-model/epic        "WIN-5734"}
               {:feature-flag/id                :feature-flag.id/external-modules
                :feature-flag-model/description "Manage products implemented by an external system (Public Works Asset Management) from tenant management, and push their users there"
                :feature-flag-model/depends-on  #{:feature-flag.id/new-tenant-management}
                :feature-flag-model/jira-issue  "WIN-5734"
                :feature-flag-model/epic        "WIN-5734"}
               {:feature-flag/id                :feature-flag.id/property-attribute-rules
                :feature-flag-model/description "Property attribute rules"
                :feature-flag-model/jira-issue  "WIN-5201"}
               {:feature-flag/id                :feature-flag.id/workspace-precheck-results
                :feature-flag-model/description "Show saved AI precheck results in the application workspace"
                :feature-flag-model/jira-issue  "WIN-5741"}
               {:feature-flag/id                :feature-flag.id/statcan-v3
                :feature-flag-model/description "Use the new StatCan residential building occupancy report format"
                :feature-flag-model/jira-issue  "WIN-5869"}
               {:feature-flag/id                :feature-flag.id/property-multiple-addresses
                :feature-flag-model/description "Enable multiple addresses per property PIN"
                :feature-flag-model/jira-issue  "WIN-5626"
                :feature-flag-model/epic        "WIN-4714"}
               {:feature-flag/id                :feature-flag.id/licence-search-updates
                :feature-flag-model/description (str "New filters in the licence search, such as expiry and issue date ranges "
                                                     "and in-progress application")
                :feature-flag-model/jira-issue  "WIN-4725"
                :feature-flag-model/epic        "WIN-4660"}
               {:feature-flag/id                :feature-flag.id/new-group-user-management
                :feature-flag-model/description "Link group admin user management to the new view"
                :feature-flag-model/jira-issue  "WIN-6077"
                :feature-flag-model/epic        "WIN-5734"}
               {:feature-flag/id                :feature-flag.id/ai-config-transfer
                :feature-flag-model/description "Transfer AI files, settings and rules in group config export/import"
                :feature-flag-model/jira-issue  "WIN-5988"}
               {:feature-flag/id                :feature-flag.id/ai-settings-controls
                :feature-flag-model/description "Separate group controls for Auto Fill, PlanCheck and NoVa"
                :feature-flag-model/jira-issue  "WIN-6007"
                :feature-flag-model/epic        "WIN-4780"}
               {:feature-flag/id                :feature-flag.id/application-wizard-location-manual-entry
                :feature-flag-model/description "Improve manual location entry in the application wizard"
                :feature-flag-model/jira-issue  "WIN-6134"
                :feature-flag-model/epic        "WIN-4777"}
               {:feature-flag/id                :feature-flag.id/contractor-registration-search-filters
                :feature-flag-model/description (str "Additional contractor registration search filters. "
                                                     "Requires reindexing the contractor registration Elasticsearch index before enabling.")
                :feature-flag-model/jira-issue  "WIN-4937"}
               {:feature-flag/id                :feature-flag.id/form-data-audit-log
                :feature-flag-model/description "Show form value history in the workspace audit log"
                :feature-flag-model/jira-issue  "WIN-5212"
                :feature-flag-model/epic        "WIN-4660"}
               {:feature-flag/id                :feature-flag.id/form-field-change-metadata
                :feature-flag-model/description "Show the latest form field value change and renewal differences"
                :feature-flag-model/jira-issue  "WIN-5775"
                :feature-flag-model/epic        "WIN-4660"}
               {:feature-flag/id                :feature-flag.id/land-use-planning-statuses
                :feature-flag-model/description "Refuse, deny, appeal and finish statuses in the Land Use process flow"
                :feature-flag-model/jira-issue  "WIN-6021"
                :feature-flag-model/epic        "WIN-4221"
                :feature-flag-model/depends-on  #{:feature-flag.id/application-deny-option
                                                  :feature-flag.id/appeal-in-more-steps}}
               {:feature-flag/id                :feature-flag.id/inspection-ai-native
                :feature-flag-model/description "Use PostgreSQL and Gemini in Vaahtera instead of the Inspector AI service"
                :feature-flag-model/jira-issue  "WIN-6009"}
               #__]))

(defn get-model [id] (p/find-first flags {:feature-flag/id id}))

(defn deps-filled? [{:feature-flag-model/keys [depends-on]} enabled-flags]
  (every? enabled-flags depends-on))

(defn depended-on-by [{:feature-flag/keys [id enabled?]} other-enabled-flags]
  (when-not enabled? ; Is flag being disabled?
    (->> flags
         (filter (fn [other-flag]
                   (and (contains? (:feature-flag-model/depends-on other-flag) id)
                        (contains? other-enabled-flags (:feature-flag/id other-flag)))))
         (map :feature-flag/id)
         seq)))

(def flag-ids (map :feature-flag/id flags))

(s/defschema FlagId (apply s/enum flag-ids))

(def flag->hidden-signals
  "Mapping of feature flags to signal-ids that should be hidden from the Options dropdown when that flag is enabled.
   If the signal should be completely disabled use :process.guard/feature-not-enabled? or :process.guard/feature-enabled?
   instead of this."
  {:feature-flag.id/property-db-units #{:process.signal/add-additional-property
                                        :process.signal/change-primary-property}})
